/**
 * The browser half of §8.1's anchor: block id, character range, and the text as
 * it read — computed from a live ProseMirror selection, and mapped back to
 * document positions so the margin can paint what a thread is about.
 *
 * ## One offset contract, two languages
 *
 * `apps/api/src/comments/anchor.ts` defines what an offset means: it indexes
 * into the block's own text, where a hard break counts as one `\n` and nested
 * blocks — a table's cells, a callout's paragraphs — are joined by one `\n`. The
 * server computes that from stored JSON; this file computes it from the
 * ProseMirror document the person is looking at. {@link blockTextMap} is
 * therefore a deliberate mirror of the server's `blockText`, walked in the same
 * order with the same two rules, and `anchor.test.ts` pins the cases where a
 * naive implementation would drift (hard breaks, marks splitting a paragraph
 * into several text nodes, structure inside a callout).
 *
 * A mirror is a cost, and the alternative was worse: computing anchors from
 * `textContent` would silently drop the `\n` a hard break contributes, and every
 * comment written after one would land a character or more to the left of the
 * words it quotes. The map is small, it is tested, and it is the only place in
 * the browser that knows what an offset means.
 *
 * ## The client re-seats; it does not judge
 *
 * {@link locateAnchor} looks for the anchored text in the *live* document, which
 * has usually moved on from the body the API resolved against — Yjs holds the
 * keystrokes for a couple of seconds before `apps/collab` snapshots them (§8.3).
 * It exists so the highlight follows the words rather than an offset, and it is
 * deliberately dumber than the server's resolver: exact slice, then a plain
 * search, then nothing.
 *
 * **It never decides that a comment is orphaned.** That is `resolveComment`'s
 * answer, computed server-side against the stored body and returned as `state`
 * and `orphanReason`, and §8.1 hangs a record-keeping obligation on it. A second
 * opinion in the browser — with a different matching rule, against a document
 * that is two seconds ahead — would eventually contradict the panel about which
 * objections still stand. So when this function finds nothing, the thread keeps
 * whatever label the API gave it and simply goes unpainted.
 */

import { ID_BEARING_TYPES } from '@matchbox/shared/markdown';
import type { Node as PMNode } from '@tiptap/pm/model';
import type { EditorState } from '@tiptap/pm/state';

/** The four fields `POST /api/comments` takes as `anchor`. */
export interface BlockAnchor {
  blockId: string;
  start: number;
  end: number;
  /** As it read when the comment was written — the durable half of the anchor. */
  text: string;
}

export interface BlockLocation {
  node: PMNode;
  /** The position of the block node itself, so `pos + 1` is where its content starts. */
  pos: number;
}

export interface DocumentRange {
  from: number;
  to: number;
}

/**
 * A block's text beside the position of every character in it.
 *
 * The two arrays are what make the mapping invertible: `starts[i]` is where
 * character `i` begins in the document and `ends[i]` is where it finishes, so an
 * offset range becomes a position range and a position becomes an offset without
 * either direction re-deriving the text.
 */
export interface BlockTextMap {
  text: string;
  starts: number[];
  ends: number[];
  /** Where the block's content begins — the answer for an anchor on an empty block. */
  contentStart: number;
}

function isInline(node: PMNode): boolean {
  // The server's `INLINE_TYPES`, in ProseMirror's vocabulary. Everything else is
  // a block for text purposes and gets a `\n` between it and its siblings.
  return node.isText || node.type.name === 'hardBreak';
}

/** The block a comment names, wherever it now sits — depth is not special-cased. */
export function findBlock(doc: PMNode, blockId: string): BlockLocation | null {
  let found: BlockLocation | undefined;

  doc.descendants((node, pos) => {
    if (found) return false;
    if (ID_BEARING_TYPES.has(node.type.name) && node.attrs?.id === blockId) {
      found = { node, pos };
      return false;
    }
    return true;
  });

  return found ?? null;
}

export function blockTextMap(block: BlockLocation): BlockTextMap {
  const map: BlockTextMap = { text: '', starts: [], ends: [], contentStart: block.pos + 1 };
  walk(block.node, block.pos, map);
  return map;
}

function walk(node: PMNode, pos: number, map: BlockTextMap): void {
  if (node.isText) {
    const value = node.text ?? '';
    // Indexed by UTF-16 code unit, which is also how a text node's `nodeSize` is
    // counted and how the server's `String.prototype.slice` reads an offset.
    // Iterating by code *point* would put an anchor half a character out on the
    // first emoji anybody types.
    for (let index = 0; index < value.length; index += 1) {
      map.text += value.charAt(index);
      map.starts.push(pos + index);
      map.ends.push(pos + index + 1);
    }
    return;
  }

  if (node.type.name === 'hardBreak') {
    map.text += '\n';
    map.starts.push(pos);
    map.ends.push(pos + 1);
    return;
  }

  const children: PMNode[] = [];
  node.forEach((child) => {
    children.push(child);
  });
  // An atom with no text of its own — an image, an embed, a live-data block —
  // contributes nothing rather than a placeholder, exactly as the server's
  // `blockText` does. A comment on one of those is a block-level anchor.
  if (children.length === 0) return;

  const inlineOnly = children.every(isInline);
  let childPos = pos + 1;

  children.forEach((child, index) => {
    if (!inlineOnly && index > 0) {
      // The separator is a character in the offset space with no character in
      // the document, so it is zero-width: both ends sit on the boundary. A
      // range that stops here stops at the end of the previous child, which is
      // what a reader selecting a paragraph means.
      map.text += '\n';
      map.starts.push(childPos);
      map.ends.push(childPos);
    }
    walk(child, childPos, map);
    childPos += child.nodeSize;
  });
}

/** The offset a document position falls at — `text.length` for a position past the end. */
export function offsetAtPosition(map: BlockTextMap, pos: number): number {
  for (let index = 0; index < map.starts.length; index += 1) {
    const start = map.starts[index];
    if (start !== undefined && start >= pos) return index;
  }
  return map.text.length;
}

function positionAtOffset(map: BlockTextMap, offset: number): number {
  const start = map.starts[offset];
  if (start !== undefined) return start;
  const last = map.ends[map.ends.length - 1];
  return last ?? map.contentStart;
}

/** The document range an offset range covers. Zero-width when `end <= start`. */
export function rangeForOffsets(map: BlockTextMap, start: number, end: number): DocumentRange {
  const from = positionAtOffset(map, Math.max(0, Math.min(start, map.text.length)));
  if (end <= start) return { from, to: from };
  const last = map.ends[Math.min(end, map.text.length) - 1];
  return { from, to: last ?? from };
}

/**
 * The anchor for what is selected right now, or null when the selection is not
 * something §8.1 can anchor to.
 *
 * The innermost id-bearing ancestor wins, so a paragraph inside a callout
 * anchors to the paragraph. A selection that runs past that block climbs to the
 * next one out — two paragraphs of one callout anchor to the callout — and a
 * selection spanning two top-level blocks has no common anchor and returns null,
 * which is what the composer explains rather than silently truncating.
 *
 * An empty selection is a block-level anchor: `text` is `''`, which the server
 * resolves as "points at the block rather than at anything inside it" and keeps
 * alive for as long as the block survives. That is the honest anchor for
 * "a comment about this paragraph".
 */
export function anchorFromSelection(state: EditorState): BlockAnchor | null {
  const { from, to } = state.selection;
  const $from = state.doc.resolve(from);

  for (let depth = $from.depth; depth > 0; depth -= 1) {
    const node = $from.node(depth);
    const id = node.attrs?.id;
    if (!ID_BEARING_TYPES.has(node.type.name) || typeof id !== 'string') continue;

    const pos = $from.before(depth);
    if (to > pos + node.nodeSize) continue;

    const map = blockTextMap({ node, pos });
    const start = offsetAtPosition(map, from);
    const end = offsetAtPosition(map, to);

    // A cursor with no selection is a comment on the block, not on a phrase, and
    // it carries the block's whole text rather than the empty slice.
    //
    // The empty slice is what it quotes; the block text is what it was written
    // against. Without the second, a block rewritten to say the opposite keeps
    // the comment in the margin beside it with no signal — the server has
    // nothing to compare and cannot report `block_changed`. Sending it costs one
    // string on a request that already carries the comment body.
    if (start === end) return { blockId: id, start, end, text: map.text };

    return { blockId: id, start, end, text: map.text.slice(start, end) };
  }

  // A selected atom — an image, an embed, a live-data block — is not inside any
  // block; it *is* one, and it has no text for a range to cover. It gets the
  // block-level anchor, which is the only kind that can point at something a
  // reader can see and a resolver cannot quote.
  const selected = state.doc.nodeAt(from);
  const selectedId = selected?.attrs?.id;
  if (
    selected &&
    typeof selectedId === 'string' &&
    ID_BEARING_TYPES.has(selected.type.name) &&
    to <= from + selected.nodeSize
  ) {
    return { blockId: selectedId, start: 0, end: 0, text: '' };
  }

  return null;
}

/**
 * What a block says, for a composer that has to name the thing being commented
 * on.
 *
 * A block-level anchor quotes nothing — that is what makes it survive a rewrite
 * — so "commenting on this block" is the only sentence the anchor itself
 * supports, and it is not enough to act on. This reads the block once, at the
 * moment the composer opens, and is never stored.
 */
export function blockPreview(doc: PMNode, blockId: string): string {
  const block = findBlock(doc, blockId);
  return block ? blockTextMap(block).text : '';
}

/**
 * Where a stored anchor sits in the document on screen, for painting only.
 *
 * Three steps, in the order that costs least and drifts least:
 *
 * 1. The stored offsets still slice out the stored text — the answer for every
 *    edit made somewhere else, which is nearly all of them.
 * 2. A block-level anchor (no text) points at the block, so it survives as long
 *    as the block does.
 * 3. The text is searched for in the block, nearest occurrence to the stored
 *    offset winning — the same tiebreak the server uses, and for the same
 *    reason: with "the Client" three times in a clause the offset is the only
 *    thing that tells them apart.
 *
 * No whitespace tolerance and no similarity threshold, which is where this
 * deliberately stops short of the server's resolver: those rules decide whether
 * an objection still stands, and that decision has one home.
 */
export function locateAnchor(doc: PMNode, anchor: BlockAnchor): DocumentRange | null {
  const block = findBlock(doc, anchor.blockId);
  if (!block) return null;

  const map = blockTextMap(block);

  if (anchor.end <= map.text.length && map.text.slice(anchor.start, anchor.end) === anchor.text) {
    return rangeForOffsets(map, anchor.start, anchor.end);
  }

  if (anchor.text.length === 0) {
    const at = Math.min(anchor.start, map.text.length);
    return rangeForOffsets(map, at, at);
  }

  let best: number | null = null;
  let bestDistance = Number.POSITIVE_INFINITY;
  for (
    let at = map.text.indexOf(anchor.text);
    at !== -1;
    at = map.text.indexOf(anchor.text, at + 1)
  ) {
    const distance = Math.abs(at - anchor.start);
    if (distance < bestDistance) {
      best = at;
      bestDistance = distance;
    }
  }
  if (best === null) return null;

  return rangeForOffsets(map, best, best + anchor.text.length);
}
